import {
  Button,
  ButtonGroup,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  TextField,
} from '@customerio/pluma-components/react';
import { useState } from 'react';
import { z } from 'zod';

import { useZodForm } from '@/components/form';
import { useCreateAsset } from '@/services/db/asset/mutations';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';

const schema = z.object({
  name: z.string().trim().min(1, 'Folder name is required'),
});

type CreateFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  parentFolderId: string | null;
  onSuccess: () => void;
};

export function CreateFolderModal({ isOpen, onClose, parentFolderId, onSuccess }: CreateFolderModalProps) {
  const { track } = useAnalytics();
  const createAssetMutation = useCreateAsset();

  const [serverError, setServerError] = useState<string | null>(null);

  const form = useZodForm({
    schema,
    defaultValues: { name: '' },
    mode: 'onSubmit',
  });

  const onSubmit: React.FormEventHandler = (event) => {
    setServerError(null);
    void handleValidSubmit(event);
  };

  const handleValidSubmit = form.handleSubmit(async (values) => {
    try {
      await createAssetMutation.mutateAsync({
        name: values.name.trim(),
        is_dir: true,
        parent_id: parentFolderId,
      });

      track('assetLibrary', 'folderCreated', {
        folder_name: values.name.trim(),
        parent_folder_id: parentFolderId,
      });

      form.reset();
      setServerError(null);
      onSuccess();
    } catch (error: unknown) {
      sentryCaptureException(error);
      if (error instanceof Error) {
        setServerError(!isEmptyString(error.message) ? error.message : 'Failed to create folder');
      } else {
        setServerError('Failed to create folder');
      }
    }
  });

  const handleClose = () => {
    onClose();
    form.reset();
    setServerError(null);
  };

  const plumaProps = form.registerPluma('name');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Name your folder" shouldShowCloseButton={true}>
      <ModalBody>
        <Form id="create-folder-form" onSubmit={onSubmit}>
          <TextField
            label="Name"
            placeholder="Folder name"
            {...plumaProps}
            error={serverError ?? plumaProps.error}
            autoFocus
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup groupVariant="spaced">
          <Button variant="secondary" onClick={handleClose} isDisabled={form.formState.isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="create-folder-form" variant="primary" isLoading={form.formState.isSubmitting}>
            Create Folder
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
}
